import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import { buildNotification, GenerateNotification, Loader } from 'smart-react';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../constants/eventDataTypes';
import {
  saveConfigurationSetting,
  getConfigurationSetting,
  verifyDBConfig,
} from '../Services/SettingService';
import { Button } from '@progress/kendo-react-buttons';
import { Switch, TextBox, RadioButton } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import PasswordField from '../../Core/Components/FormFields/PasswordField';
import './Setting.scss';
import {
  TabStrip,
  TabStripTab,
  Card,
  CardBody,
} from '@progress/kendo-react-layout';
import TextBoxWithSuggestions from '../../Core/Components/FormFields/TextBoxWithSuggestions';
import * as Icons from 'lucide-react';
import './Setting.scss';
/**
 * Rollout main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Rollout component.
 */
const Setting = () => {
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(0);
  const handleSelect = (e) => {
    setSelected(e.selected);
  };

  const [configuration, setConfiguration] = React.useState([]);

  const [envObj, setEnvObj] = React.useState({});
  const [authMode, setAuthMode] = React.useState({});
  const [selectedAuthTab, setSelectedAuthTab] = React.useState(0);
  const [dbVerified, setDBVerified] = React.useState(true);
  const [updateConfiguration, setUpdateConfiguration] = React.useState({});
  const [yamlOrder, setYamlOrder] = React.useState([]);
  const [yamlNewToken, setYamlNewToken] = React.useState('');
  const [dragIndex, setDragIndex] = React.useState(null);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);
  /**
   * Description: Function show error toast
   * @param er string
   * @return
   */
  const showError = (er) => {
    GenerateNotification(
      buildNotification({
        title: 'Something went wrong',
        description: er,
        style: 'error',
      }),
      NOTIFICATION_TYPES.APP,
      EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
    );
  };

  /**
   * Description: function for handle input change
   * @param e object
   * @return
   */
  const handleInput = (e) => {
    if (e.target.name.includes('DB_')) {
      setDBVerified(false);
    }
    setUpdateConfiguration((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
    setEnvObj((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  /**
   * Description: function for making sub state state
   * @param data array
   * @param config_data array
   * @return
   */
  const makeSubSectionState = (data, config_data) => {
    Object.keys(data).map((item) => {
      if (
        Object.prototype.propertyIsEnumerable.call(
          data[item].Properties,
          'subsection'
        )
      ) {
        Object.keys(data[item]).map((subSectionKey, a) => {
          if (subSectionKey !== 'Properties') {
            setAuthMode((prevAuthState) => ({
              ...prevAuthState,
              [subSectionKey]:
                config_data[subSectionKey] === '1' ? true : false,
            }));
            Object.keys(data[item][subSectionKey]).map((indexKey) => {
              if (data[item][subSectionKey][indexKey].type === 'file') {
                setEnvObj((prevState) => ({
                  ...prevState,
                  [indexKey]: { file: '', image: config_data[indexKey] },
                }));
              }
            });
          }
        });
      } else {
        Object.keys(data[item]).map((subSectionKey) => {
          if (subSectionKey !== 'Properties') {
            if (data[item][subSectionKey].type === 'file') {
              setEnvObj((prevState) => ({
                ...prevState,
                [subSectionKey]: {
                  file: '',
                  image: config_data[subSectionKey],
                },
              }));
            }
          }
        });
      }
    });
  };

  /**
   * Description: function for fetch configuration from api
   * @return
   */
  const fetchConfiguration = async () => {
    setLoading(true);
    try {
      const response = await getConfigurationSetting();
      setEnvObj(response.config_data);
      makeSubSectionState(response.data, response.config_data);
      setConfiguration(response.data);

      // Initialize YAML order from configuration data
      const cfgOrderRaw = response.config_data?.REFS_YAML_LOAD_ORDER;
      console.log('cfgOrderRaw', cfgOrderRaw);
      if (cfgOrderRaw) {
        try {
          const parsed = JSON.parse(cfgOrderRaw);
          if (Array.isArray(parsed)) {
            setYamlOrder(parsed);
          }
        } catch (e) {
          // ignore parse errors and leave default
        }
      } else {
        setYamlOrder([]);
      }
      setLoading(false);
    } catch (error) {
      showError(error);
      setLoading(false);
    }
  };
  /**
   * Description: function for save configuration
   * @param e object
   * @return
   */
  const saveConfig = async (e) => {
    e.preventDefault();
    if (!dbVerified) {
      showError('Please verify Database Configuration');
      return;
    }
    setLoading(true);
    const fileType = 'rolloutconfig';
    try {
      const payload = {
        ...updateConfiguration,
        REFS_YAML_LOAD_ORDER: JSON.stringify(yamlOrder || []),
      };
      const response = await saveConfigurationSetting({
        envVarArray: payload,
        type: fileType,
      });
      GenerateNotification(
        buildNotification({
          title: 'Successfully Saved',
          description: 'Your changes have been saved successfully.',
          style: 'success',
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
      setLoading(false);
    } catch (error) {
      showError(error);
      setLoading(false);
    }
  };

  /**
   * Description: function for verifing Database configuration
   * @param e object
   * @return
   */
  const verifyDBConfiguration = async (e) => {
    setLoading(true);
    const dbConfigObj = {
      DB_CONNECTION: envObj.DB_CONNECTION,
      DB_HOST: envObj.DB_HOST,
      DB_PORT: envObj.DB_PORT,
      DB_DATABASE: envObj.DB_DATABASE,
      DB_SID: envObj.DB_SID,
      DB_USERNAME: envObj.DB_USERNAME,
      DB_PASSWORD: envObj.DB_PASSWORD,
    };
    setLoading(true);
    try {
      const response = await verifyDBConfig({ data: dbConfigObj });
      setDBVerified(response.success);

      if (response.success === true) {
        GenerateNotification(
          buildNotification({
            title: 'Verify Successfully',
            description: response.message,
            style: 'success',
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      } else {
        showError(response.message);
      }
      setLoading(false);
    } catch (error) {
      showError(error);
      setLoading(false);
    }
  };

  /**
   * Description: function for making tab content
   * @param obj object
   * @return HTML content
   */

  const tabContent = (obj) => {
    const IconComponent = Icons[obj.Properties.Icon];

    if (
      Object.prototype.propertyIsEnumerable.call(obj.Properties, 'subsection')
    ) {
      return (
        <div>
          {/* Card Header */}
          <div className="card-header">
            <h1 className="card-title">
              {IconComponent && <IconComponent />}
              {obj.Properties.Name}
            </h1>
            <p className="card-subtitle">
              Configure authentication methods and providers
            </p>
          </div>

          {/* Authentication Methods Tabs */}
          <div className="auth-methods-tabs">
            <TabStrip
              className="auth-tabstrip"
              selected={selectedAuthTab}
              onSelect={(e) => setSelectedAuthTab(e.selected)}
              tabPosition={'top'}
            >
              {Object.keys(obj).map((item, a) => {
                if (item !== 'Properties') {
                  const authIcon = getAuthIcon(item);
                  return (
                    <TabStripTab
                      key={a}
                      title={
                        <div className="auth-tab-content">
                          {authIcon && <authIcon />}
                          <span>
                            {obj[item].Properties.Name.replace(
                              'AUTH ',
                              ''
                            ).replace(' Configuration', '')}
                          </span>
                        </div>
                      }
                    >
                      <div className="auth-method-content">
                        {/* Enable/Disable Toggle */}
                        <div className="auth-toggle-section">
                          <div className="toggle-wrapper">
                            <span className="toggle-label">
                              Enable{' '}
                              {obj[item].Properties.Name.replace(
                                'AUTH ',
                                ''
                              ).replace(' Configuration', '')}
                            </span>
                            <Switch
                              checked={authMode[item]}
                              onChange={(e) => {
                                setAuthMode((prevAuthState) => ({
                                  ...prevAuthState,
                                  [item]: !prevAuthState[item],
                                }));
                                setUpdateConfiguration((prevState) => ({
                                  ...prevState,
                                  [item]: authMode[item] ? '0' : '1',
                                }));
                              }}
                              size="medium"
                            />
                          </div>
                        </div>

                        {/* Configuration Fields - Only show if enabled */}
                        {authMode[item] && (
                          <div className="field-main">
                            {Object.keys(obj[item]).map((indexKey, i) => {
                              const field = obj[item][indexKey];

                              // Check if field has dependency
                              const hasDependency =
                                field.depend_on && field.visible_if;
                              let isVisible = true;

                              if (hasDependency) {
                                // Get dependent field value
                                const dependentValue =
                                  obj[item][field.depend_on];

                                // Support multiple visible_if values (comma or array)
                                const visibleValues = Array.isArray(
                                  field.visible_if
                                )
                                  ? field.visible_if
                                  : field.visible_if
                                      .split(',')
                                      .map((v) => v.trim());

                                isVisible =
                                  visibleValues.includes(dependentValue);
                              }

                              return indexKey !== 'Properties' && isVisible ? (
                                <div key={i} className="field-wrapper">
                                  <Label>{getLabelName(field)}</Label>
                                  {renderElement(field, indexKey)}
                                  {field.suggestion_text && (
                                    <div className="helper-text">
                                      {field.suggestion_text}
                                    </div>
                                  )}
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    </TabStripTab>
                  );
                }
                return null;
              })}
            </TabStrip>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
            }}
          >
            <Button themeColor={'primary'} onClick={(e) => saveConfig(e)}>
              Save Configuration
            </Button>
          </div>
        </div>
      );
    } else {
      // Handle non-subsection content
      return (
        <div>
          {/* Card Header */}
          <div className="card-header">
            <h1 className="card-title">
              {IconComponent && <IconComponent />}
              {obj.Properties.Name} Configuration
            </h1>
            <p className="card-subtitle">
              Configure your {obj.Properties.Name.toLowerCase()} settings
            </p>
          </div>

          <div className="field-main">
            {Object.keys(obj).map((item, a) => {
              const field = obj[item];

              // ✅ Check if field has dependency and if it matches
              let shouldRender = true;

              if (field.depend_on && field.visible_if) {
                const dependentValue = envObj[field.depend_on];

                // Handle multiple visible_if values (string or array)
                const visibleValues = Array.isArray(field.visible_if)
                  ? field.visible_if
                  : field.visible_if
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean); // removes empty values

                shouldRender = visibleValues.includes(dependentValue);
              }

              return (
                item !== 'Properties' &&
                shouldRender && (
                  <div key={a} className="field-wrapper">
                    <Label>{getLabelName(field)}</Label>
                    {renderElement(field, item)}
                    {field.suggestion_text && (
                      <div className="helper-text">{field.suggestion_text}</div>
                    )}
                  </div>
                )
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
            }}
          >
            <Button themeColor={'primary'} onClick={(e) => saveConfig(e)}>
              Save Configuration
            </Button>
            {obj.Properties.Name === 'Database Configuration' && (
              <Button
                themeColor={'primary'}
                onClick={(e) => verifyDBConfiguration(e)}
              >
                Verify DB Configuration
              </Button>
            )}
          </div>
        </div>
      );
    }
  };

  /**
   * Description: function for handle image change
   * @param e object
   * @return
   */
  const handleImageChange = (e) => {
    e.preventDefault();

    let reader = new FileReader();
    let file = e.target.files[0];

    reader.onloadend = () => {
      setUpdateConfiguration((prevState) => ({
        ...prevState,
        [e.target.name]: { file, image: reader.result },
      }));
      setEnvObj((prevState) => ({
        ...prevState,
        [e.target.name]: { file, image: reader.result },
      }));
    };

    reader.readAsDataURL(file);
  };

  /**
   * YAML Order helpers
   */
  const addYamlToken = () => {
    const t = (yamlNewToken || '').trim();
    if (!t) return;
    // prevent duplicates
    if (yamlOrder.includes(t)) {
      setYamlNewToken('');
      return;
    }
    setYamlOrder((prev) => [...prev, t]);
    setYamlNewToken('');
  };

  const deleteYamlToken = (index) => {
    setYamlOrder((prev) => prev.filter((_, i) => i !== index));
  };

  const moveYaml = (index, dir) => {
    setYamlOrder((prev) => {
      const arr = [...prev];
      const newIndex = dir === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      const [m] = arr.splice(index, 1);
      arr.splice(newIndex, 0, m);
      return arr;
    });
  };

  const saveYamlOrder = () => {
    const serialized = JSON.stringify(yamlOrder || []);
    // reflect in env object for completeness
    setEnvObj((prev) => ({ ...prev, REFS_YAML_LOAD_ORDER: serialized }));
    // stage into updateConfiguration so it persists with Save Configuration
    setUpdateConfiguration((prev) => ({
      ...prev,
      REFS_YAML_LOAD_ORDER: serialized,
    }));
    GenerateNotification(
      buildNotification({
        title: 'YAML order updated',
        description: 'REFS YAML load order staged for saving.',
        style: 'success',
      }),
      NOTIFICATION_TYPES.APP,
      EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
    );
  };

  // Drag & Drop handlers for YAML order
  const onDragStart = (index) => {
    setDragIndex(index);
  };
  const onDragOver = (index, e) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const onDragLeave = () => {
    setDragOverIndex(null);
  };
  const onDrop = (index) => {
    if (dragIndex === null) return;
    setYamlOrder((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIndex, 1);
      let target = index;
      if (dragIndex < index) target = index - 1;
      if (target < 0) target = 0;
      arr.splice(target, 0, moved);
      return arr;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  /**
   * Description: function for showing input element
   * @param item object
   * @param itemName string
   * @return HTML conetent
   */

  const renderElement = (item, itemName) => {
    // check visibility rules
    if (item.depend_on && item.visible_if) {
      const parentValue = envObj[item.depend_on];

      // Support multiple visible_if values (string or array)
      const visibleValues = Array.isArray(item.visible_if)
        ? item.visible_if
        : item.visible_if
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean); // remove empty strings

      // Only render if parentValue matches any of visible_if values
      if (!visibleValues.includes(parentValue)) {
        return null; // don't render if condition fails
      }
    }

    if (item.type === 'input') {
      return (
        <div style={{ position: 'relative' }}>
          <TextBoxWithSuggestions
            id={itemName}
            name={itemName}
            placeholder={itemName}
            value={envObj[itemName]}
            onChange={handleInput}
            suggestions={
              item?.suggestion_value?.length > 0 ? item?.suggestion_value : []
            }
            suggestionText={
              item?.suggestion_text?.length > 0
                ? item?.suggestion_text
                : 'Enter a valid value'
            }
          />
        </div>
      );
    }

    if (item.type === 'password') {
      return (
        <div style={{ position: 'relative' }}>
          <PasswordField
            id={itemName}
            name={itemName}
            placeholder={itemName}
            value={envObj[itemName]}
            onChange={handleInput}
          />
        </div>
      );
    }

    if (item.type === 'select') {
      if (item.dependent_key && item.options) {
        let subArray = [];
        item.options.split(',').forEach((obj1) => {
          if (obj1 && obj1.includes('|')) {
            let arr1 = obj1.split('|');
            if (arr1.length >= 2 && arr1[1]) {
              subArray[arr1[0]] = arr1[1].split('.');
            }
          }
        });
        return (
          <select
            name={itemName}
            className="select-field"
            onChange={handleInput}
            value={envObj[itemName] || ''}
          >
            <option value="">Select provider</option>
            {subArray[envObj[item.dependent_key]] &&
              subArray[envObj[item.dependent_key]]
                .filter((n) => n)
                .map((listItem) => (
                  <option value={listItem} key={listItem}>
                    {listItem}
                  </option>
                ))}
          </select>
        );
      }

      if (item.options) {
        return (
          <select
            className="select-field"
            name={itemName}
            onChange={handleInput}
            value={envObj[itemName] || ''}
          >
            <option value="" disabled>
              Select provider
            </option>
            {item.options
              .split(',')
              .filter((n) => n)
              .map((listItem) => (
                <option value={listItem} key={listItem}>
                  {listItem}
                </option>
              ))}
          </select>
        );
      }
    }

    if (item.type === 'file') {
      return (
        <div className="previewComponent">
          <input
            className="fileInput"
            name={itemName}
            type="file"
            onChange={(e) => handleImageChange(e)}
          />
          <div className="imgPreview">
            <img
              src={envObj[itemName]?.image?.replace(/['"]+/g, '')}
              alt={itemName}
            />
          </div>
        </div>
      );
    }

    if (item.type === 'radio') {
      return (
        <div className="radio-wrapper">
          {item.options.split(',').filter((n) => n).length === 2 ? (
            <Switch
              checked={envObj[itemName] === item.options.split(',')[0]} // first option is considered "ON"
              onChange={(e) =>
                handleInput({
                  target: {
                    name: itemName,
                    value: e.value
                      ? item.options.split(',')[0]
                      : item.options.split(',')[1], // toggle between 2 options
                  },
                })
              }
              size="medium"
              onLabel={item.options.split(',')[0]} // optional: show labels
              offLabel={item.options.split(',')[1]}
            />
          ) : (
            item.options
              .split(',')
              .filter((n) => n)
              .map((radio, index) => (
                <RadioButton
                  key={index}
                  value={radio}
                  label={radio}
                  inputId={`radio-${itemName}-${index}`}
                  checked={radio === envObj[itemName]}
                  onChange={(e) =>
                    handleInput({ target: { name: itemName, value: radio } })
                  }
                />
              ))
          )}
        </div>
      );
    }

    if (item.type === 'checkbox') {
      return (
        <div className="checkbox-wrapper">
          <div className="checkbox-item">
            <input
              type="checkbox"
              id={`checkbox-${itemName}`}
              name={itemName}
              checked={envObj[itemName] === 'true' || envObj[itemName] === true}
              onChange={(e) =>
                handleInput({
                  target: {
                    name: itemName,
                    value: e.target.checked ? 'true' : 'false',
                  },
                })
              }
              className="custom-checkbox"
            />
            <label htmlFor={`checkbox-${itemName}`} className="checkbox-label">
              {item.name || itemName}
            </label>
          </div>
          {item.suggestion_text && (
            <div className="helper-text">{item.suggestion_text}</div>
          )}
        </div>
      );
    }
    if (item.type === 'yamlorder') {
      return (
        <div className="yamlorder-editor">
          <div className="yamlorder-toolbar">
            <TextBox
              placeholder="Add new item..."
              value={yamlNewToken}
              onChange={(e) => setYamlNewToken(e.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addYamlToken();
              }}
            />
            <Button
              themeColor={'primary'}
              className="add-btn"
              onClick={() => addYamlToken()}
              title="Add"
            >
              +
            </Button>
          </div>
          <ul className="yamlorder-list">
            {yamlOrder.map((tok, idx) => (
              <li
                key={`${tok}-${idx}`}
                className={`yamlorder-item ${dragOverIndex === idx ? 'drag-over' : ''} ${dragIndex === idx ? 'dragging' : ''}`}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={(e) => onDragOver(idx, e)}
                onDragLeave={onDragLeave}
                onDrop={() => onDrop(idx)}
                onDragEnd={onDragEnd}
              >
                {Icons.GripVertical && (
                  <Icons.GripVertical className="drag-handle" size={16} />
                )}
                <span className="token">{tok}</span>
                <Button
                  className="icon-btn danger"
                  onClick={() => deleteYamlToken(idx)}
                  title="Delete"
                >
                  {Icons.Trash2 ? <Icons.Trash2 size={16} /> : 'Delete'}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };

  const getLabelName = (item) => item?.name;

  // Helper function to get authentication method icons
  const getAuthIcon = (authMethod) => {
    const iconMap = {
      AUTH_DATABASE: Icons.Database,
      AUTH_AZURE: Icons.Cloud,
      AUTH_GOOGLE: Icons.Mail,
      AUTH_OKTA: Icons.Shield,
      AUTH_LDAP: Icons.Users,
    };
    return iconMap[authMethod];
  };
  React.useEffect(() => {
    fetchConfiguration();
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <React.Suspense fallback={<Loader />}>
        <div className="setting-page">
          {loading && <Loader />}
          <TabStrip
            className="page-tabs"
            selected={selected}
            onSelect={handleSelect}
            tabPosition={'top'}
          >
            {Object.keys(configuration).map((item, i) => {
              const IconComponent = Icons[configuration[item].Properties.Icon];

              return (
                <TabStripTab
                  key={i}
                  title={
                    <div className="tab-title-content">
                      {IconComponent && <IconComponent />}
                      <span>{configuration[item].Properties.Name}</span>
                    </div>
                  }
                >
                  <Card className="tabs-card">
                    <CardBody>{tabContent(configuration[item])}</CardBody>
                  </Card>
                </TabStripTab>
              );
            })}
          </TabStrip>
        </div>
      </React.Suspense>
    </ErrorBoundary>
  );
};
export default Setting;
